/**
 * Order-status → presentation mapping (§18 scannability). The single place that
 * decides which semantic colour ("tone") and human label an OrderStatus gets,
 * so tabs, row chips, and the drawer are always consistent. Pure + DOM-free →
 * unit-tested in the node vitest project. Colours live in tokens.css keyed by
 * the tone (`.chip--<tone>`); this module never references a raw colour.
 */
import type { OrderStatus, ServiceClass } from "@cairn/shared";

/** The fixed palette of status meanings (one hue each). */
export type StatusTone =
  | "new"
  | "routed"
  | "ready"
  | "hold"
  | "exception"
  | "done"
  | "cancelled";

/**
 * Every OrderStatus mapped to a tone. In-progress states fold into `routed`
 * (blue), terminal "shipped/closed" states into `done` (violet), waiting states
 * into `hold` (amber). The six the manager triages on day-to-day (NEW, ROUTED,
 * READY, ON_HOLD, EXCEPTION, FULFILLED) each get their own distinct hue.
 */
export const STATUS_TONE: Record<OrderStatus, StatusTone> = {
  NEW: "new",
  ROUTED: "routed",
  IN_BATCH: "routed",
  PACKING: "routed",
  PACKED: "routed",
  READY: "ready",
  READY_FOR_PICKUP: "ready",
  FULFILLED: "done",
  PARTIALLY_FULFILLED: "done",
  PICKED_UP: "done",
  ON_HOLD: "hold",
  AWAITING_STOCK: "hold",
  AWAITING_CS_DECISION: "hold",
  EXCEPTION: "exception",
  CANCELLED: "cancelled",
};

/** Tone for a status, defaulting to a neutral `new` for anything unexpected. */
export function statusTone(status: string): StatusTone {
  return STATUS_TONE[status as OrderStatus] ?? "new";
}

/** Short human labels for chips/tabs (avoids shouting RAW_ENUM at the user). */
export const STATUS_LABEL: Record<OrderStatus, string> = {
  NEW: "New",
  ROUTED: "Routed",
  IN_BATCH: "In batch",
  PACKING: "Packing",
  PACKED: "Packed",
  READY: "Ready",
  READY_FOR_PICKUP: "Ready · pickup",
  FULFILLED: "Fulfilled",
  PARTIALLY_FULFILLED: "Part. fulfilled",
  PICKED_UP: "Picked up",
  ON_HOLD: "On hold",
  AWAITING_STOCK: "Awaiting stock",
  AWAITING_CS_DECISION: "Awaiting CS",
  EXCEPTION: "Exception",
  CANCELLED: "Cancelled",
};

/** Human label for a status; falls back to a Title-cased form of the raw enum. */
export function statusLabel(status: string): string {
  const known = STATUS_LABEL[status as OrderStatus];
  if (known) return known;
  return status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

/**
 * Whether a service class is a priority/express class — drives the queue's
 * express-first visual emphasis (§30). EXPRESS and PRIORITY qualify; STANDARD /
 * ECONOMY do not. Null/unknown → not priority.
 */
export function isPriorityClass(serviceClass: ServiceClass | string | null | undefined): boolean {
  return serviceClass === "EXPRESS" || serviceClass === "PRIORITY";
}

/**
 * Whether an order can be sent to the pack station now (Task 1 quick-pack). Ready
 * orders and ones already in a batch / mid-pack qualify; new/held/terminal ones
 * do not. Drives the queue-row + drawer "Pack" action.
 */
const PACKABLE: ReadonlySet<string> = new Set(["READY", "IN_BATCH", "PACKING"]);
export function isPackable(status: string | null | undefined): boolean {
  return status != null && PACKABLE.has(status);
}
